/**
 * Generated impulse responses (no samples, audio-direction §7): seeded, deterministic, built once at engine
 * start. `cockpitIR` is a short, dense, metallic cabin (the hull chain's convolution); `hallIR` is the long,
 * dark space the score sits in (organ, taiko, brass). Both are decorrelated stereo exponential noise tails
 * that darken as they decay, plus (cockpit) a few ringing cabin modes.
 */
import { createRng, nextFloat, type Rng } from '../core/rng'

export interface IrSpec {
  seconds: number
  predelay: number
  /** Seconds to −60 dB. */
  decay: number
  /** Low-pass of the tail at its start; it darkens to a quarter of that by the end. */
  lowpassHz: number
  /** Cabin resonances (Hz) that ring through the tail (none for halls). */
  modes: readonly number[]
}

/** Fills stereo channels `l`, `r` with the IR described by `spec`. */
export function fillIR(l: Float32Array, r: Float32Array, rate: number, spec: IrSpec, rng: Rng): void {
  const n = l.length
  const pre = Math.min(n, Math.floor(spec.predelay * rate))
  const k = Math.log(1000) / Math.max(spec.decay * rate, 1)
  let yl = 0
  let yr = 0
  const phases = spec.modes.map(() => nextFloat(rng) * Math.PI * 2)
  const modeGain = spec.modes.length > 0 ? 0.35 / Math.sqrt(spec.modes.length) : 0
  for (let i = pre; i < n; i++) {
    const t = (i - pre) / Math.max(n - pre, 1)
    // One-pole low-pass whose cutoff falls to a quarter over the tail (air and panels absorb the highs first).
    const fc = spec.lowpassHz * (1 - 0.75 * t)
    const a = 1 - Math.exp((-2 * Math.PI * fc) / rate)
    yl += a * (nextFloat(rng) * 2 - 1 - yl)
    yr += a * (nextFloat(rng) * 2 - 1 - yr)
    const env = Math.exp(-k * (i - pre))
    let ring = 0
    for (let m = 0; m < spec.modes.length; m++) ring += Math.sin(phases[m] + (2 * Math.PI * spec.modes[m] * (i - pre)) / rate)
    l[i] = (yl + ring * modeGain) * env
    r[i] = (yr + ring * modeGain * 0.9) * env
  }
}

/** An IR as an `AudioBuffer` of `ac` (stereo). */
export function createIR(ac: BaseAudioContext, spec: IrSpec, seed: number): AudioBuffer {
  const length = Math.max(1, Math.ceil(spec.seconds * ac.sampleRate))
  const buffer = ac.createBuffer(2, length, ac.sampleRate)
  fillIR(buffer.getChannelData(0), buffer.getChannelData(1), ac.sampleRate, spec, createRng(seed))
  return buffer
}
